import { create } from 'zustand';
import { apiClient } from '@/services/api';
import type { Game } from '@/types';

let fetchGamesPromise: Promise<void> | null = null;

interface GameState {
  games: Game[];
  hasLoaded: boolean;
  selectedGame: Game | null;
  isLoading: boolean;
  error: string | null;
  fetchGames: (force?: boolean) => Promise<void>;
  selectGame: (game: Game | null) => void;
  createGame: (gameData: Partial<Game>) => Promise<void>;
  updateGame: (gameId: string, gameData: Partial<Game>) => Promise<void>;
  deleteGame: (gameId: string) => Promise<void>;
  clearError: () => void;
}

export const useGameStore = create<GameState>((set, get) => ({
  games: [],
  hasLoaded: false,
  selectedGame: null,
  isLoading: false,
  error: null,

  fetchGames: async (force = false) => {
    if (!force && get().hasLoaded) {
      return;
    }

    if (fetchGamesPromise) {
      return fetchGamesPromise;
    }

    set({ isLoading: true, error: null });
    fetchGamesPromise = (async () => {
      try {
        const response = await apiClient.getAllGames();
        if (response.success) {
          set({ games: response.data, hasLoaded: true, isLoading: false });
        } else {
          throw new Error(response.error || 'Failed to fetch games');
        }
      } catch (error: any) {
        set({ 
          error: error.response?.data?.message || error.message || 'Failed to fetch games', 
          isLoading: false 
        });
      } finally {
        fetchGamesPromise = null;
      }
    })();

    return fetchGamesPromise;
  },

  selectGame: (game: Game | null) => {
    set({ selectedGame: game });
  },

  createGame: async (gameData: Partial<Game>) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.createGame(gameData);
      if (response.success) {
        set({ 
          games: [...get().games, response.data], 
          isLoading: false 
        });
      } else {
        throw new Error(response.error || 'Failed to create game');
      }
    } catch (error: any) {
      set({ 
        error: error.response?.data?.message || error.message || 'Failed to create game', 
        isLoading: false 
      });
      throw error;
    }
  },

  updateGame: async (gameId: string, gameData: Partial<Game>) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.updateGame(gameId, gameData);
      if (response.success) {
        set({ 
          games: get().games.map(g => g.id === gameId ? response.data : g), 
          isLoading: false 
        });
      } else {
        throw new Error(response.error || 'Failed to update game');
      }
    } catch (error: any) {
      set({ 
        error: error.response?.data?.message || error.message || 'Failed to update game', 
        isLoading: false 
      });
      throw error;
    }
  },

  deleteGame: async (gameId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.deleteGame(gameId);
      if (response.success) {
        set({ 
          games: get().games.filter(g => g.id !== gameId), 
          isLoading: false 
        });
      } else {
        throw new Error(response.error || 'Failed to delete game');
      }
    } catch (error: any) {
      set({ 
        error: error.response?.data?.message || error.message || 'Failed to delete game', 
        isLoading: false 
      });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));

